import React, { useEffect, useMemo, useState } from 'react';
import { Alert, DialogActions, DialogContent, DialogTitle, Snackbar } from '@mui/material';
import { DialogProps } from '../DialogProps';
import { DecodedVehicleInformation } from '../../../Data/DecodedVehicleInformation';
import { isNil } from 'ramda';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import styles from './_trade-in-vehicle-dialog.module.scss';
import { DatePickerWithYearSanitizer, hasValue, PhoneInput, TradeInVehicle } from '@amsanalytics/react-shared-library';
import { NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Error as KendoError } from '@progress/kendo-react-labels';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { getDefaultTradeInStockNumber, validateStockNumber } from '../../../Data/VehicleData';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import { IState } from '../../../Interfaces/Common/IState';

function formatStockNumberConflict(): string {
  return 'Stock number already exists.';
}

export interface TradeInVehicleDialogProps extends DialogProps<TradeInVehicle> {
  vehicle?: TradeInVehicle; // the vehicle to edit
  defaultBalance?: number; // the default balance to use if no vehicle is provided
  vehicleOfSaleStockNumber?: string; // stock number of vehicle of sale, used to generate default trade-in stock number
  tradeInNumber?: 1 | 2; // which trade-in slot (default 1)
}

export default function TradeInVehicleDialog(props: TradeInVehicleDialogProps) {
  const tradeInNumber = props.tradeInNumber ?? 1;
  const isEditing = hasValue(props.vehicle?.id);
  const [vehicle, setVehicle] = useState<Partial<TradeInVehicle>>(
    props.vehicle ? { ...props.vehicle } : { tradeBalance: props.defaultBalance }
  );
  const [errors, setErrors] = useState<Error[]>([]);
  const [submitButtonClicked, setSubmitButtonClicked] = useState<boolean>(false);
  const [isDecodingVin, setIsDecodingVin] = useState<boolean>(false);
  const [isDecodeSnackbarOpen, setIsDecodeSnackbarOpen] = useState<boolean>(false);
  const [decodeSnackbarMessage, setDecodeSnackbarMessage] = useState<string>('');
  const [hasFocusedAcv, setHasFocusedAcv] = useState<boolean>(false);
  const [stockNumberError, setStockNumberError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const allStates: IState[] = useSelector((state: AppState) => state.applications.states);
  const states = useMemo(() => [...allStates].sort((a, b) => a.abbreviation > b.abbreviation ? 1 : -1), [allStates]);
  const selectedState = states.find(s => s.id === vehicle.lienHolderStateId) || null;

  useEffect(() => {
    if (!props.vehicle && props.vehicleOfSaleStockNumber) {
      getDefaultTradeInStockNumber(props.vehicleOfSaleStockNumber, tradeInNumber).then((defaultStockNumber) => {
        if (defaultStockNumber) {
          setVehicle((prev) => ({ ...prev, stockNumber: defaultStockNumber }));
        }
      });
    }
  }, [props.vehicle, props.vehicleOfSaleStockNumber]);

  const updateVehicle = (updates: Partial<TradeInVehicle>) => {
    setVehicle((prev) => ({ ...prev, ...updates }));
  };

  const hasFieldError = (fieldName: string) =>
    submitButtonClicked && errors.some((error) => error.name === fieldName);

  const getFieldErrorMessage = (fieldName: string) =>
    submitButtonClicked ? errors.find((error) => error.name === fieldName)?.message : undefined;

  const isValidVehicle = (vehicle: Partial<TradeInVehicle>): boolean => {
    const validationErrors: Error[] = [];
    if (!hasValue(vehicle.make)) {
      const error = new Error('Make is required');
      error.name = 'Make';
      validationErrors.push(error);
    }
    if (!hasValue(vehicle.model)) {
      const error = new Error('Model is required');
      error.name = 'Model';
      validationErrors.push(error);
    }
    if (!hasValue(vehicle.year)) {
      const error = new Error('Year is required');
      error.name = 'Year';
      validationErrors.push(error);
    }
    if (!hasValue(vehicle.tradeBalance)) {
      const error = new Error('Trade Owed is required');
      error.name = 'TradeBalance';
      validationErrors.push(error);
    }
    if (!hasValue(vehicle.tradeValue)) {
      const error = new Error('Trade Value is required');
      error.name = 'Value';
      validationErrors.push(error);
    }
    if (!hasValue(vehicle.acv)) {
      const error = new Error('Acv is required');
      error.name = 'ACV';
      validationErrors.push(error);
    }
    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const getVehicleInformationByVin = async () => {
    try {
      setIsDecodingVin(true);
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vehicle.vin}?format=json`);
      if (!response.ok) throw new Error(response.statusText);

      const vinInfo: { Results: { Value: string; ValueId: string; Variable: string; VariableId: number }[] } =
        await response.json();
      const hasError = vinInfo?.Results.find((r) => r.VariableId === 143)?.Value !== '0';

      if (!hasError) {
        const yearResult = vinInfo.Results.find((r) => r.VariableId === 29)?.Value;
        const make: string = vinInfo.Results.find((r) => r.VariableId === 26)?.Value as string;
        const model: string = vinInfo.Results.find((r) => r.VariableId === 28)?.Value as string;
        const year: number = yearResult ? parseInt(yearResult) : 0;
        return { year, make, model };
      } else {
        return undefined;
      }
    } catch (error: any) {
      return undefined;
    }
  };

  const handleDecodeVinClick = async () => {
    await getVehicleInformationByVin()
      .then((decodedInfo: DecodedVehicleInformation | undefined) => {
        if (!isNil(decodedInfo)) {
          updateVehicle({
            year: decodedInfo.year,
            make: decodedInfo.make,
            model: decodedInfo.model,
          });
          setDecodeSnackbarMessage('VIN decoded successfully.');
        } else {
          setDecodeSnackbarMessage('VIN could not be decoded. Please double-check the VIN and try again.');
        }
      })
      .finally(() => {
        setIsDecodeSnackbarOpen(true);
        setIsDecodingVin(false);
      });
  };

  const handleSave = async () => {
    setSubmitButtonClicked(true);
    if (!isValidVehicle(vehicle)) return;

    setIsSaving(true);
    try {
      // Advisory stock number validation
      if (hasValue(vehicle.stockNumber)) {
        const validation = await validateStockNumber(vehicle.stockNumber!, vehicle.id);
        if (!validation.isAvailable) {
          setStockNumberError(formatStockNumberConflict());
          return;
        }
      }

      setStockNumberError('');
      props.accept && props.accept(vehicle as TradeInVehicle);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (submitButtonClicked) {
      isValidVehicle(vehicle);
    }
  }, [vehicle, submitButtonClicked]);

  return (
    <>
      <DialogTitle>{isEditing ? `Edit Trade-In ${tradeInNumber} Vehicle` : `Add Trade-In ${tradeInNumber} Vehicle`}</DialogTitle>
      <DialogContent dividers className={styles.dialogContent}>
        <div className={styles.tradeInVehicleDialogContainer}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Vehicle Details</div>
            <div className={styles.vinRow}>
              <div className={styles.vinField}>
                <div className={styles.fieldLabel}>VIN</div>
                <TextBox
                  fillMode='outline'
                  style={{ width: '100%' }}
                  value={vehicle.vin || ''}
                  onChange={(e) => updateVehicle({ vin: e.value as string })}
                  inputAttributes={{ maxLength: 17 }}
                />
              </div>
              <Button
                themeColor='primary'
                fillMode='outline'
                className={styles.decodeButton}
                onClick={handleDecodeVinClick}
                disabled={isDecodingVin || !hasValue(vehicle.vin)}
              >
                <span className={styles.decodeButtonContent}>
                  <ContentPasteSearchIcon className={styles.decodeButtonIcon} />
                  <span>{isDecodingVin ? 'Decoding...' : 'Decode VIN'}</span>
                </span>
              </Button>
            </div>
            <div className={styles.fieldGrid}>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Stock Number</div>
                <TextBox
                  fillMode='outline'
                  style={{ width: '100%' }}
                  value={vehicle.stockNumber || ''}
                  onChange={(e) => {
                    updateVehicle({ stockNumber: e.value as string });
                    setStockNumberError('');
                  }}
                  valid={!stockNumberError}
                  inputAttributes={{ maxLength: 15 }}
                />
                {stockNumberError && <KendoError>{stockNumberError}</KendoError>}
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Year *</div>
                <NumericTextBox
                  fillMode='outline'
                  value={vehicle.year}
                  format='####'
                  style={{ width: '100%' }}
                  onChange={(e) => updateVehicle({ year: e.value ?? undefined })}
                  valid={!hasFieldError('Year')}
                />
                {getFieldErrorMessage('Year') && <KendoError>{getFieldErrorMessage('Year')}</KendoError>}
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Make *</div>
                <TextBox
                  fillMode='outline'
                  style={{ width: '100%' }}
                  value={vehicle.make || ''}
                  onChange={(e) => updateVehicle({ make: e.value as string })}
                  valid={!hasFieldError('Make')}
                />
                {getFieldErrorMessage('Make') && <KendoError>{getFieldErrorMessage('Make')}</KendoError>}
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Model *</div>
                <TextBox
                  fillMode='outline'
                  style={{ width: '100%' }}
                  value={vehicle.model || ''}
                  onChange={(e) => updateVehicle({ model: e.value as string })}
                  valid={!hasFieldError('Model')}
                />
                {getFieldErrorMessage('Model') && <KendoError>{getFieldErrorMessage('Model')}</KendoError>}
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Color</div>
                <TextBox
                  fillMode='outline'
                  style={{ width: '100%' }}
                  value={vehicle.color || ''}
                  onChange={(e) => updateVehicle({ color: e.value as string })}
                />
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Body</div>
                <TextBox
                  fillMode='outline'
                  style={{ width: '100%' }}
                  value={vehicle.body || ''}
                  onChange={(e) => updateVehicle({ body: e.value as string })}
                />
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Mileage</div>
                <NumericTextBox
                  fillMode='outline'
                  value={vehicle.mileage}
                  format='n0'
                  style={{ width: '100%' }}
                  onChange={(e) => updateVehicle({ mileage: e.value ?? undefined })}
                />
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Trade Values</div>
            <div className={styles.valueGrid}>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Trade Owed *</div>
                <NumericTextBox
                  fillMode='outline'
                  value={vehicle.tradeBalance}
                  format='c2'
                  style={{ width: '100%' }}
                  onChange={(e) => updateVehicle({ tradeBalance: e.value ?? 0 })}
                  valid={!hasFieldError('TradeBalance')}
                />
                {getFieldErrorMessage('TradeBalance') && <KendoError>{getFieldErrorMessage('TradeBalance')}</KendoError>}
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>Trade Allowance *</div>
                <NumericTextBox
                  fillMode='outline'
                  value={vehicle.tradeValue}
                  format='c2'
                  style={{ width: '100%' }}
                  onChange={(e) =>
                    updateVehicle({
                      tradeValue: e.value ?? 0,
                      acv: !hasFocusedAcv || isNil(vehicle.acv) ? (e.value ?? 0) : vehicle.acv,
                    })
                  }
                  valid={!hasFieldError('Value')}
                />
                {getFieldErrorMessage('Value') && <KendoError>{getFieldErrorMessage('Value')}</KendoError>}
              </div>
              <div className={styles.textField}>
                <div className={styles.fieldLabel}>ACV *</div>
                <NumericTextBox
                  fillMode='outline'
                  style={{ width: '100%' }}
                  value={vehicle.acv}
                  format='c2'
                  onFocus={() => {
                    if (!hasFocusedAcv) setHasFocusedAcv(true);
                  }}
                  onChange={(e) => updateVehicle({ acv: e.value ?? 0 })}
                  valid={!hasFieldError('ACV')}
                />
                {getFieldErrorMessage('ACV') && <KendoError>{getFieldErrorMessage('ACV')}</KendoError>}
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Lien Holder</div>
            <div className={styles.fieldGrid}>
                    <div className={styles.textField}>
                      <div className={styles.fieldLabel}>Lien Holder Name</div>
                      <TextBox
                        fillMode='outline'
                        style={{ width: '100%' }}
                        value={vehicle.lienHolderName || ''}
                        onChange={(e) => updateVehicle({ lienHolderName: e.value as string })}
                        inputAttributes={{ maxLength: 100 }}
                      />
                    </div>
                    <div className={styles.textField}>
                      <div className={styles.fieldLabel}>Lien Holder Address</div>
                      <TextBox
                        fillMode='outline'
                        style={{ width: '100%' }}
                        value={vehicle.lienHolderAddress || ''}
                        onChange={(e) => updateVehicle({ lienHolderAddress: e.value as string })}
                        inputAttributes={{ maxLength: 200 }}
                      />
                    </div>
                    <div className={styles.textField}>
                      <div className={styles.fieldLabel}>Lien Holder City</div>
                      <TextBox
                        fillMode='outline'
                        style={{ width: '100%' }}
                        value={vehicle.lienHolderCity || ''}
                        onChange={(e) => updateVehicle({ lienHolderCity: e.value as string })}
                        inputAttributes={{ maxLength: 100 }}
                      />
                    </div>
                    <div className={styles.textField}>
                      <div className={styles.fieldLabel}>Lien Holder State</div>
                      <DropDownList
                        fillMode='outline'
                        data={states}
                        textField="name"
                        dataItemKey="id"
                        value={selectedState}
                        onChange={(e: DropDownListChangeEvent) => updateVehicle({ lienHolderStateId: e.target.value?.id })}
                        defaultItem={{ id: null, name: '' }}
                      />
                    </div>
                    <div className={styles.textField}>
                      <div className={styles.fieldLabel}>Lien Holder Zip</div>
                      <TextBox
                        fillMode='outline'
                        style={{ width: '100%' }}
                        value={vehicle.lienHolderZip || ''}
                        onChange={(e) => updateVehicle({ lienHolderZip: e.value as string })}
                        inputAttributes={{ maxLength: 10 }}
                      />
                    </div>
                    <div className={styles.textField}>
                      <div className={styles.fieldLabel}>Lien Holder Phone</div>
                      <PhoneInput
                        fillMode='outline'
                        value={vehicle.lienHolderPhone || ''}
                        onChangeValue={(val) => updateVehicle({ lienHolderPhone: val.replace(/\D/g, '') })}
                      />
                    </div>
                    <div className={styles.textField}>
                      <div className={styles.fieldLabel}>Payoff Good Through Date</div>
                      <DatePickerWithYearSanitizer
                        fillMode='outline'
                        style={{ width: '100%' }}
                        value={vehicle.payoffGoodThroughDate ? new Date(vehicle.payoffGoodThroughDate) : null}
                        onChange={(e: DatePickerChangeEvent) => updateVehicle({ payoffGoodThroughDate: e.value ?? undefined })}
                      />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button fillMode='outline' onClick={props.closeDialog}>
          Cancel
        </Button>
        <Button themeColor='primary' fillMode='solid' onClick={handleSave} disabled={isSaving || !!stockNumberError}>
          {isSaving ? 'Validating...' : isEditing ? 'Save Changes' : 'Create Vehicle'}
        </Button>
      </DialogActions>
      <Snackbar
        open={isDecodeSnackbarOpen}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setIsDecodeSnackbarOpen(false)}
      >
        <Alert onClose={() => setIsDecodeSnackbarOpen(false)} severity='info' sx={{ width: '100%' }}>
          {decodeSnackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
